import { type CoreComponentProps } from "@khol/utils/pages/core-component";
import {type CorePageNextProps } from "@khol/utils/pages/core-page-next";
import {
  type LanguageProvided,
  type TextDefinitions,
} from "@khol/utils/languages/defineTexts";
import {  type CODE_LANGUAGE } from "@khol/utils/languages/language";
import React from "react";

export function withTranslations<D extends Readonly<TextDefinitions>>(
  Component: (props: { texts: any; params: any; searchParams: any }) => any,
  definedTexts: D
) {
  return async function Wrapped({ params, searchParams }: CorePageNextProps) {
    const resolvedParams = await params;
    const resolvedSearchParams = await searchParams;
    const texts = Object.entries(definedTexts).reduce(
      (acc: any, [key, value]) => {
        if (typeof value === "function") {
          acc[key] = (...p: any) => value(...p)[resolvedParams.lang];
        } else acc[key] = value[resolvedParams.lang];
        return acc;
      },
      {}
    );
    return (
      <Component texts={texts} params={resolvedParams} searchParams={resolvedSearchParams} />
    );
  };
}

export function withTranslationsClient<
  T extends CoreComponentProps,
  D extends Readonly<TextDefinitions>,
>(Component: (props: T & { texts: any }) => any, definedTexts: D) {
  return function Wrapped(props: Omit<T, "texts">) {
    const locale: CODE_LANGUAGE = props.locale || "en";
    const texts = Object.entries(definedTexts).reduce(
      (acc: any, [key, value]) => {
        if (typeof value === "function") {
          acc[key] = (...p: any) => value(...p)[locale];
        } else acc[key] = value[locale];
        return acc;
      },
      {}
    );
    return <Component {...(props as T)} texts={texts} />;
  };
}

type ExtractFunctionParams<T> = T extends (...args: infer P) => any ? P : never;

export type TextProps<D extends TextDefinitions, T = {}> = {
  texts: {
    [key in keyof D]: D[key] extends LanguageProvided
      ? string
      : (...p: ExtractFunctionParams<D[key]>) => string;
  };
} & CorePageNextProps<T>;

export type TextClientProps<D extends TextDefinitions> = {
  texts: {
    [key in keyof D]: D[key] extends LanguageProvided
      ? string
      : (...p: ExtractFunctionParams<D[key]>) => string;
  };
} & CoreComponentProps;
